import type { WorkReportAppendixImage } from '../types/workReport';

/** Parse Unterzeichnender lines from field-app work-report API body. */
export function parseSignerNameLines(input: {
  signerName?: unknown;
  signerNameLines?: unknown;
}): string[] {
  if (Array.isArray(input.signerNameLines)) {
    const lines = input.signerNameLines
      .map((entry) => String(entry ?? '').trim())
      .filter(Boolean);
    if (lines.length > 0) return lines;
  }
  const single = String(input.signerName ?? '').trim();
  if (!single) return [];
  return single
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export const WORK_REPORT_APPENDIX_IMAGE_MAX_COUNT = 30;
export const WORK_REPORT_APPENDIX_IMAGE_MAX_BYTES = 4 * 1024 * 1024;
/**
 * Shared decoded-byte cap for **all** images in one work-report POST
 * (appendix + checklist photos). Fits in WORK_REPORT_JSON_BODY_LIMIT with base64 overhead.
 */
export const WORK_REPORT_APPENDIX_MAX_TOTAL_BYTES = 40 * 1024 * 1024;
/** Must match server.ts route-specific express.json limit for work-report POST. */
export const WORK_REPORT_JSON_BODY_LIMIT = "55mb";

const DATA_IMAGE_RE = /^data:image\/(jpeg|jpg|png|webp);base64,/i;
const IMAGE_FILE_NAME_RE = /^(?:.*[/\\])?[^/\\]+\.(?:jpe?g|png|webp|gif|heic|heif|bmp|tiff?)$/i;

/** True when a caption is just an image file name (e.g. 289.jpg). */
export function isImageFileNameCaption(caption: string): boolean {
  return IMAGE_FILE_NAME_RE.test(caption.trim());
}

function normalizeAppendixCaption(raw: unknown): string | null {
  const value = String(raw ?? '').trim();
  if (!value || isImageFileNameCaption(value)) return null;
  return value;
}

export function workReportDataImageByteLength(src: string): number {
  const base64 = src.split(',')[1] ?? '';
  if (!base64) return 0;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

export function isValidWorkReportDataImage(raw: unknown): raw is string {
  const src = String(raw ?? '').trim();
  if (!DATA_IMAGE_RE.test(src)) return false;
  const byteLength = workReportDataImageByteLength(src);
  return byteLength > 0 && byteLength <= WORK_REPORT_APPENDIX_IMAGE_MAX_BYTES;
}

export type ParseWorkReportAppendixImagesResult =
  | { ok: true; images: WorkReportAppendixImage[] }
  | { ok: false; error: string };

/** Parse optional appendix images from field-app work-report API body. */
export function parseWorkReportAppendixImages(input: {
  appendixImages?: unknown;
}): ParseWorkReportAppendixImagesResult {
  const raw = input.appendixImages;
  if (raw == null) return { ok: true, images: [] };
  if (!Array.isArray(raw)) {
    return { ok: false, error: 'appendixImages muss ein Array sein.' };
  }
  if (raw.length > WORK_REPORT_APPENDIX_IMAGE_MAX_COUNT) {
    return {
      ok: false,
      error: `Maximal ${WORK_REPORT_APPENDIX_IMAGE_MAX_COUNT} Anlagenbilder erlaubt.`,
    };
  }

  const images: WorkReportAppendixImage[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const entry = raw[i];
    let src = '';
    let caption: string | null = null;

    if (typeof entry === 'string') {
      src = entry.trim();
    } else if (entry && typeof entry === 'object') {
      const row = entry as Record<string, unknown>;
      src = String(row.src ?? row.image ?? row.dataUrl ?? '').trim();
      caption = normalizeAppendixCaption(row.caption ?? row.label);
    } else {
      return { ok: false, error: `Anlagenbild ${i + 1} ist ungültig.` };
    }

    if (!src) {
      return { ok: false, error: `Anlagenbild ${i + 1} fehlt.` };
    }
    if (!isValidWorkReportDataImage(src)) {
      return {
        ok: false,
        error: `Anlagenbild ${i + 1} ist ungültig oder zu groß (max. ${Math.round(WORK_REPORT_APPENDIX_IMAGE_MAX_BYTES / (1024 * 1024))} MB, JPEG/PNG/WebP).`,
      };
    }

    images.push({ src, caption });
  }

  const totalBytes = images.reduce((sum, img) => sum + workReportDataImageByteLength(img.src), 0);
  if (totalBytes > WORK_REPORT_APPENDIX_MAX_TOTAL_BYTES) {
    return {
      ok: false,
      error: `Anlagenbilder gesamt zu groß (max. ${Math.round(WORK_REPORT_APPENDIX_MAX_TOTAL_BYTES / (1024 * 1024))} MB).`,
    };
  }

  return { ok: true, images };
}

/** Decoded byte total for appendix images (for shared budget with checklist photos). */
export function sumWorkReportAppendixImageBytes(images: WorkReportAppendixImage[]): number {
  return images.reduce((sum, img) => sum + workReportDataImageByteLength(img.src), 0);
}
